import {
  Box,
  Button,
  Flex,
  Icon,
  InputDescription,
  InputGroup,
  OptionType,
  Spinner,
} from '@fullstory/components';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { AuditMetadata, Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { SegmentSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { SegmentDetail } from '@fullstory/proto/fs/services/dx-objects';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import {
  AppDispatch,
  fullstorySelectors,
  getCustomSegmentKey,
  getCustomSegmentLabel,
  getDefaultSegmentScope,
  IDs,
  SegmentRequestActions,
  segmentsSelectors,
  useSegmentAccessDispatch,
  useSegmentRequestState,
} from '@fullstory/redux';
import React, { ComponentProps, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useStarredRecentResources } from '../../hooks';
import { auditMetadataToDisplayMetadata } from '../../utils';
import { DEFAULT_SEGMENT } from '../../utils/constants';
import { getSelectedItemLabel } from '../../utils/utils';
import { Metadata } from '../MetadataDisplay';
import { ResourceSelectMenu } from '../ResourceSelectMenu';
import { useFlags } from '@fullstory/react-hooks/useFlags';
import { getSegmentsAccess } from '@fullstory/redux/modules/segments/selectors';
import { VisibilityLabel } from '../VisibilityLabel';
import { intl } from '@fullstory/intl';

const { getAllSegments, getSegmentsById } = segmentsSelectors;

export type SegmentOption = {
  id?: string;
  definition?: SegmentDefinition;
  metadata?: AuditMetadata;
  starred: boolean;
  lastViewed?: Date;
};

const getMetadata = (opt: OptionType<SegmentOption>): Metadata | null =>
  auditMetadataToDisplayMetadata(opt.value.metadata);

export interface SegmentSelectProps
  extends Pick<ComponentProps<typeof ResourceSelectMenu>, 'variant'>,
    Pick<ComponentProps<typeof ResourceSelectMenu>, 'size'> {
  selected: SegmentSettings | null;
  filterSegments?: SegmentSettings[];
  formError?: string;
  fullWidth?: boolean;
  maxSegments?: number;
  placeholder?: string;
  showIcon?: boolean;
  showClearButton?: boolean;
  allowPrivateSegments?: boolean;
  onCreate?: () => void;
  onEdit?: (segment: OptionType<SegmentOption>) => void;
  renderDefinition?: (option: OptionType<SegmentOption>) => ReactNode;
}

/* We only ever want to allow `onChange` to
 return `null` if it is explicitly set to be clearable
 */
export interface ClearableProps extends SegmentSelectProps {
  clearable: true;
  onChange: (value: SegmentSettings | null) => void;
}

export interface DefaultProps extends SegmentSelectProps {
  clearable?: never;
  onChange: (value: SegmentSettings) => void;
}

export type SegmentSelectPropType = ClearableProps | DefaultProps;

export const SegmentSelect: React.FC<SegmentSelectPropType> = props => {
  const {
    onChange,
    onCreate,
    onEdit,
    selected,
    filterSegments,
    formError,
    fullWidth = true,
    variant = 'fill-builder',
    size = 'medium',
    renderDefinition,
    clearable,
    placeholder,
    showIcon,
    showClearButton,
    allowPrivateSegments = false,
  } = props;
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const segments = useSelector(getAllSegments);
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const byId = useSelector(getSegmentsById);
  const { isReady: segmentsReady } = useSegmentRequestState();
  const flags = useFlags();

  const isReady = useMemo(() => {
    // Short circut the loading state if selected is passed. We can show
    // the text on the button immediately while segments load in the background.
    if (selected) {
      return true;
    }

    return segmentsReady;
  }, [segmentsReady, selected]);

  const fetchAllSegments = useCallback(() => {
    if (!segmentsReady) {
      dispatch(SegmentRequestActions.getAllSegments(orgId));
    }
  }, [segmentsReady, dispatch, orgId]);

  useEffect(() => {
    if (!isReady) fetchAllSegments();
  }, [fetchAllSegments, isReady]);

  const customSegments: OptionType<SegmentOption>[] = useMemo(() => {
    if (Array.isArray(selected)) {
      return selected.filter(segment => !segment.segmentId).map(customSegmentToOption);
    } else if (selected && !selected.segmentId) {
      return [customSegmentToOption(selected)];
    }
    return [];
  }, [selected]);

  const segmentOptions = useMemo(
    () =>
      segments
        .filter(
          segment =>
            !segment.segment?.metadata?.archived ||
            selected?.segmentId === segment.segment.segment?.id,
        )
        .map(segment => segmentDetailToOption(segment, allowPrivateSegments))
        .concat(customSegments),
    [segments, customSegments, selected?.segmentId, allowPrivateSegments],
  );

  const filteredSegments = useMemo(() => {
    if (!filterSegments || !filterSegments.length) return segmentOptions;
    const filterMap = filterSegments.reduce<{ [key: string]: boolean }>((result, value) => {
      if (value.segmentId) {
        result[value.segmentId] = true;
      }
      return result;
    }, {});
    return segmentOptions.filter(segment => !filterMap[segment.key]);
  }, [segmentOptions, filterSegments]);

  const { recent, starred, other } = useStarredRecentResources<OptionType<SegmentOption>>({
    items: filteredSegments,
    getItemProps,
    otherLabel: intl.formatMessage({ defaultMessage: 'All Segments', id: 'w0Fuwa' }),
  });

  const optionList = useMemo(() => [...starred, ...recent, ...other], [starred, recent, other]);

  const selectedOption: OptionType<SegmentOption> | null = useMemo(() => {
    if (!selected?.segmentId) {
      return optionList.find(option => option.key === getCustomSegmentKey()) || null;
    }
    const selectedSegment = byId?.[selected.segmentId];
    if (!selectedSegment) {
      return null;
    }
    const selectedOption = optionList.find(
      option => option.key === selectedSegment.segment!.segment!.id,
    );
    return selectedOption ?? segmentDetailToOption(selectedSegment, allowPrivateSegments);
  }, [selected?.segmentId, byId, optionList, allowPrivateSegments]);

  const onChangeSelect = (segmentOption: OptionType<SegmentOption> | null) => {
    if (!segmentOption) {
      if (props.clearable) {
        return props.onChange(null);
      }
      return onChange(DEFAULT_SEGMENT);
    }
    if (segmentOption?.value?.id) {
      const segment = byId?.[segmentOption.value.id];
      const segmentScope = getDefaultSegmentScope(segment?.segment?.segment?.segmentDefinition);
      onChange({ segmentId: segmentOption.value.id, segmentScope });
    } else {
      const segmentScope = getDefaultSegmentScope(segmentOption.value.definition);
      onChange({ segmentDefinition: segmentOption.value.definition, segmentScope });
    }
  };

  // keep track of the segment IDs that need to be checked for access controls
  const [accessIds, setAccessIds] = useState<IDs>();
  // updating the state leads to dispatching the requests
  useSegmentAccessDispatch(accessIds, orgId, flags);
  const access = useSelector(getSegmentsAccess);

  const canEdit = useCallback(
    (opt: OptionType<SegmentOption>) => {
      // if there's no ID, this is a custom segment, and should be able to be edited
      if (!opt.value.id) {
        return !!opt.value.definition;
      }
      if (access.loading) return 'loading';
      if (opt.value.id !== accessIds?.stringIds?.ids?.[0] && !access.data?.access?.[opt.value.id]) {
        // only set the access IDs to load if the data doesn't exist, and if it's different
        setAccessIds({
          stringIds: {
            ids: [opt.value.id],
          },
        });
      }
      return access.data?.access?.[opt.value.id]?.canUpdate ?? false;
    },
    [access, accessIds, setAccessIds],
  );

  if (!isReady)
    return (
      <Box height={36}>
        <Flex alignItems="center" justifyContent="flex-start" style={{ height: '100%' }}>
          <Spinner />
        </Flex>
      </Box>
    );
  return (
    <Box data-component="SegmentSelect" onClick={fetchAllSegments} width="100%" overflow="hidden">
      <InputGroup>
        {clearable && showClearButton && selected && (
          <Button
            testId="clear-segment-select"
            onClick={() => onChangeSelect(null)}
            variant={variant}
            size={size}
          >
            <Icon color="activated" name="close" size={16} />
          </Button>
        )}
        <ResourceSelectMenu<SegmentOption>
          testId="segment-select"
          options={optionList}
          selected={selectedOption}
          placeholder={
            placeholder ?? intl.formatMessage({ defaultMessage: 'Choose Segment', id: 'BEw78t' })
          }
          filterPlaceholder={intl.formatMessage({ defaultMessage: 'Choose Segment', id: 'BEw78t' })}
          itemType="Segment"
          iconLeft={showIcon ? 'segments' : null}
          renderDefinition={renderDefinition}
          getMetadata={getMetadata}
          onChange={onChangeSelect}
          onCreate={onCreate}
          onEdit={onEdit}
          rightAdornment={selectedOption?.rightAdornment}
          canEdit={canEdit}
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          clearable={clearable ?? selected?.segmentId !== DEFAULT_SEGMENT.segmentId}
        />
      </InputGroup>
      {!!formError && <InputDescription invalid description={formError} />}
    </Box>
  );
};

export const getItemProps = (item: OptionType<SegmentOption>) => ({
  id: item.key,
  label: item.label,
  starred: item.value.starred,
  lastViewed: item.value.lastViewed,
});

export const segmentDetailToOption = (
  segmentDetail: SegmentDetail,
  allowPrivateSegments: boolean,
): OptionType<SegmentOption> => ({
  label: getSelectedItemLabel({
    name: segmentDetail.segment!.segment!.name,
    archived: segmentDetail.segment?.metadata?.archived ?? false,
  }),
  key: segmentDetail.segment!.segment!.id,
  value: {
    id: segmentDetail.segment!.segment!.id,
    definition: segmentDetail.segment!.segment!.segmentDefinition!,
    metadata: segmentDetail.segment!.metadata!,
    starred: segmentDetail.starred,
    lastViewed: Timestamp.toDate(segmentDetail.lastViewed) || undefined,
  },
  icon: 'segments',
  disabled:
    segmentDetail.segment?.metadata?.visibility === Visibility.PRIVATE && !allowPrivateSegments,
  rightAdornment: (
    <VisibilityLabel
      visibility={segmentDetail.segment?.metadata?.visibility}
      showLock
      showPill={false}
      tooltipContent={intl.formatMessage({ defaultMessage: 'Private segment', id: 'G9oCra' })}
    />
  ),
});

export const customSegmentToOption = (
  segment: SegmentSettings,
  index?: number,
): OptionType<SegmentOption> => {
  const definition = (segment.segmentDefinition ||
    segment.namedSegmentDefinition?.segmentDefinition) as SegmentDefinition;
  return {
    key: getCustomSegmentKey(index),
    label: getCustomSegmentLabel(index, segment),
    icon: 'segments',
    value: {
      definition,
      starred: false,
    },
  };
};
